import React from 'react';
import { Nullable } from 'global';
import { ChargePriceInput, DiscountType, QuoteDepositType } from '@trnsact/trnsact-shared-types';
import { DeleteOutline } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { CurrencyInputField, InputField, NumberFormatField, SelectField } from 'components/form/rhf';
import { Column } from 'components/shared/TableListing/types';
import type { QuoteEquipmentResponse, QuoteEquipmentTradeInsResponse } from '@trnsact/trnsact-shared-types';
import { removeGraphQLMEtaFields } from 'utils/removeGraphQLMEtaFields';
import { CustomerContactItem, DealBuilderQuote, InventoryEquipmentItem } from '../api';
import { MAX_ADDITIONAL_RECIPIENTS, STEP_CONFIG, TRADE_IN_YEAR_OPTIONS } from '../constants';
import {
  CalculateDealBuilderSummaryInput,
  DealBuilderContact,
  DealBuilderContactsData,
  DealBuilderEquipmentSource,
  DealBuilderFormValues,
  DealBuilderStep,
  DealBuilderSummary,
  GetTradeInListingColumnsParams,
  SendEmailOption,
  TradeInListingItem,
} from '../types';
import { EquipmentDelivery, equipmentConditions } from 'modules/equipment';
import { VendorOpportunity } from 'modules/vendorOpportunity';
import { FinanceProgramFormValues, CcEmailOptionGroups } from 'modules/desking/types';
import { QuoteCalcViewModel } from 'modules/quote';
import { NetTradeCell } from '../ui/steps/pricing/NetTradeCell';

export const getStepIndex = (step: DealBuilderStep): number => STEP_CONFIG.findIndex(config => config.step === step);

// Avoids float drift (e.g. 3899.7799999999997) from repeated addition/subtraction of currency values
export const roundToCents = (value: number): number => Math.round((value + Number.EPSILON) * 100) / 100;

export const calculateDealBuilderSummary = ({
  hardCost,
  feesAndTaxes,
  depositValue,
  depositType,
  tradeIn,
}: CalculateDealBuilderSummaryInput): DealBuilderSummary => {
  const customerDeposit =
    depositType === QuoteDepositType.Percentage
      ? ((depositValue ?? 0) / 100) * (hardCost + feesAndTaxes)
      : (depositValue ?? 0);

  const balanceDue = hardCost + feesAndTaxes - customerDeposit - tradeIn;

  return { hardCost, feesAndTaxes, customerDeposit, tradeIn, balanceDue };
};

export const mapQuoteCalcToSummary = (priceCalc: Nullable<QuoteCalcViewModel>): DealBuilderSummary => {
  const net = priceCalc?.netFinanceAmount;

  return {
    hardCost: net?.hardCost ?? 0,
    feesAndTaxes: roundToCents((net?.fees ?? 0) + (net?.taxes ?? 0)),
    customerDeposit: net?.deposit ?? 0,
    tradeIn: net?.netTradeIn ?? 0,
    balanceDue: net?.balanceToFinance ?? 0,
  };
};

export const calculateNetTrade = ({
  tradeInValue,
  amountOwed,
  otherCharges,
}: Pick<TradeInListingItem, 'tradeInValue' | 'amountOwed' | 'otherCharges'>): number =>
  roundToCents((tradeInValue ?? 0) - (amountOwed ?? 0) - (otherCharges ?? 0));

export const getTradeInListingColumns = ({
  control,
  onFieldBlur,
  onRemoveRow,
}: GetTradeInListingColumnsParams): Column<TradeInListingItem>[] => [
  {
    header: 'Make',
    renderKey: 'make',
    width: '250px',
    renderCell: ({ rowIndex }) => (
      <InputField
        name={`pricing.tradeIn.${rowIndex}.make`}
        control={control}
        inputProps={{ placeholder: 'Make', variant: 'outlined', size: 'small', onBlur: () => onFieldBlur(rowIndex) }}
      />
    ),
  },
  {
    header: 'Model',
    renderKey: 'model',
    width: '250px',
    renderCell: ({ rowIndex }) => (
      <InputField
        name={`pricing.tradeIn.${rowIndex}.model`}
        control={control}
        inputProps={{ placeholder: 'Model', variant: 'outlined', size: 'small', onBlur: () => onFieldBlur(rowIndex) }}
      />
    ),
  },
  {
    header: 'Year',
    renderKey: 'year',
    width: '130px',
    renderCell: ({ rowIndex }) => (
      <SelectField
        name={`pricing.tradeIn.${rowIndex}.year`}
        control={control}
        variant="outlined"
        size="small"
        options={TRADE_IN_YEAR_OPTIONS}
        extraAction={() => onFieldBlur(rowIndex)}
      />
    ),
  },
  {
    header: 'Condition',
    renderKey: 'newUsed',
    width: '160px',
    renderCell: ({ rowIndex }) => (
      <SelectField
        name={`pricing.tradeIn.${rowIndex}.newUsed`}
        control={control}
        variant="outlined"
        size="small"
        options={equipmentConditions}
        extraAction={() => onFieldBlur(rowIndex)}
      />
    ),
  },
  {
    header: 'Serial/VIN#',
    renderKey: 'serialNumberVin',
    width: '250px',
    renderCell: ({ rowIndex }) => (
      <InputField
        name={`pricing.tradeIn.${rowIndex}.serialNumberVin`}
        control={control}
        inputProps={{ variant: 'outlined', size: 'small', onBlur: () => onFieldBlur(rowIndex) }}
      />
    ),
  },
  {
    header: 'Mileage',
    renderKey: 'mileage',
    width: '150px',
    renderCell: ({ rowIndex }) => (
      <NumberFormatField
        name={`pricing.tradeIn.${rowIndex}.mileage`}
        control={control}
        label="Mileage"
        textFieldProps={{ variant: 'outlined', size: 'small', onBlur: () => onFieldBlur(rowIndex) }}
      />
    ),
  },
  {
    header: 'Trade-in Value',
    renderKey: 'tradeInValue',
    width: '150px',
    renderCell: ({ rowIndex }) => (
      <CurrencyInputField
        name={`pricing.tradeIn.${rowIndex}.tradeInValue`}
        control={control}
        textFieldProps={{ variant: 'outlined', size: 'small', onBlur: () => onFieldBlur(rowIndex) }}
      />
    ),
  },
  {
    header: 'Amount Owed',
    renderKey: 'amountOwed',
    width: '150px',
    renderCell: ({ rowIndex }) => (
      <CurrencyInputField
        name={`pricing.tradeIn.${rowIndex}.amountOwed`}
        control={control}
        textFieldProps={{ variant: 'outlined', size: 'small', onBlur: () => onFieldBlur(rowIndex) }}
      />
    ),
  },
  {
    header: 'Other Charges',
    renderKey: 'otherCharges',
    width: '150px',
    renderCell: ({ rowIndex }) => (
      <CurrencyInputField
        name={`pricing.tradeIn.${rowIndex}.otherCharges`}
        control={control}
        textFieldProps={{ variant: 'outlined', size: 'small', onBlur: () => onFieldBlur(rowIndex) }}
      />
    ),
  },
  {
    header: 'Net Trade',
    renderKey: 'netTrade',
    width: '150px',
    renderCell: ({ rowIndex }) => <NetTradeCell control={control} rowIndex={rowIndex} />,
  },
  {
    header: 'Action',
    renderKey: 'action',
    width: '80px',
    renderCell: ({ rowIndex }) => (
      <IconButton size="small" onClick={() => onRemoveRow(rowIndex)} aria-label="Remove trade-in row">
        <DeleteOutline fontSize="small" color="error" />
      </IconButton>
    ),
  },
];

export const isStepUnlocked = (step: DealBuilderStep, progressStep: DealBuilderStep): boolean =>
  getStepIndex(step) <= getStepIndex(progressStep);

export const getNextStep = (step: DealBuilderStep): DealBuilderStep =>
  STEP_CONFIG[Math.min(getStepIndex(step) + 1, STEP_CONFIG.length - 1)].step;

export const getPrevStep = (step: DealBuilderStep): DealBuilderStep =>
  STEP_CONFIG[Math.max(getStepIndex(step) - 1, 0)].step;

const mapDealBuilderContact = (contact: CustomerContactItem): DealBuilderContact => ({
  ownerPgId: contact.ownerPgDetails?.ownerPgId ?? null,
  accountContactId: contact.accountContactDetails?.id ?? null,
  fullName: [contact.firstName, contact.lastName].filter(Boolean).join(' ') || 'Unknown Contact',
  title: contact.title ?? null,
  email: contact.email ?? contact.accountContactDetails?.email ?? null,
  phone: contact.phone ?? null,
});

export const getContactId = (contact: DealBuilderContact): string =>
  contact.ownerPgId ?? contact.accountContactId ?? '';

const isPrimaryContact = (contact: CustomerContactItem, creditAppId: Nullable<string>): boolean =>
  contact.contactType === 'OWNER_PG' &&
  !!contact.ownerPgDetails?.pc &&
  !!creditAppId &&
  contact.ownerPgDetails?.creditApplicationId === creditAppId;

export const collectDealBuilderContactsData = (
  vo: Nullable<VendorOpportunity>,
  contacts: CustomerContactItem[],
  creditAppId: Nullable<string>
): DealBuilderContactsData => {
  const account = vo?.potentialCustomer
    ? { name: vo.potentialCustomer.name ?? null, address: vo.potentialCustomer.address ?? null }
    : null;

  const allContacts = contacts.map(mapDealBuilderContact);

  const primaryRaw = contacts.find(c => isPrimaryContact(c, creditAppId));
  const primaryContact = primaryRaw ? mapDealBuilderContact(primaryRaw) : null;

  const additionalRecipients = contacts
    .filter(c => !isPrimaryContact(c, creditAppId))
    .slice(0, MAX_ADDITIONAL_RECIPIENTS)
    .map(mapDealBuilderContact);

  return { account, primaryContact, additionalRecipients, allContacts };
};

export const getContactLabel = (contact: SendEmailOption, includeEmail = false): string => {
  const label = `${contact.fullName}${contact.title ? `, ${contact.title}` : ''}`;
  return includeEmail ? `${label} - ${contact.email}` : label;
};

export const getContactInitials = (fullName: string): string =>
  fullName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase())
    .join('');

export const isSameDeliveryAddress = (a: EquipmentDelivery | null, b: EquipmentDelivery) =>
  !!a &&
  a.streetAddress === b.streetAddress &&
  a.city === b.city &&
  a.state === b.state &&
  a.postalCode === b.postalCode &&
  a.country === b.country;

export const mapEquipmentSourceToItem = (source: DealBuilderEquipmentSource): InventoryEquipmentItem => {
  const name = [source.year, source.make, source.model].filter(Boolean).join(' ');

  return {
    id: source.equipmentId,
    equipment: {
      id: source.equipmentId,
      assets: source.assets,
      price: {
        retailPrice: source.retailPrice ?? null,
        wholesaleCost: source.wholesaleCost ?? null,
        totalWholesaleCost: null,
      },
      details: {
        make: source.make ?? '',
        vin: source.serialNumberVin ?? '',
        year: source.year ?? '',
        model: source.model ?? '',
        name,
        class: '',
        mileage: source.mileage != null ? String(source.mileage) : '',
        stockId: source.stockId ?? '',
        condition: source.newUsed ?? '',
        description: source.additionalDescription ?? '',
      },
    },
  };
};

// The default (no markup/discount/tax) equipment price used when the equipment is first attached to the quote
const buildDefaultChargePrice = (retailPrice: number, wholesalePrice: number): ChargePriceInput => ({
  retailPrice,
  wholesalePrice,
  netPrice: retailPrice,
  total: retailPrice,
  markup: 0,
  markupType: DiscountType.Percent,
  discount: 0,
  discountType: DiscountType.Percent,
  salesTax: 0,
  salesTaxType: DiscountType.Percent,
});

export const mapEquipmentItemToUpdateQuoteEquipmentInput = (item: InventoryEquipmentItem) => {
  const retailPrice = item.equipment.price.retailPrice ?? 0;
  const wholesalePrice = item.equipment.price.wholesaleCost ?? 0;

  return {
    equipmentId: item.equipment.id,
    cost: retailPrice,
    stockNumber: item.equipment.details.stockId,
    vin: item.equipment.details.vin,
    externalId: '',
    attributes: [],
    price: buildDefaultChargePrice(retailPrice, wholesalePrice),
  };
};

export const mapQuoteToUpdateQuoteInput = (
  quote: Nullable<DealBuilderQuote>,
  equipmentEntry: ReturnType<typeof mapEquipmentItemToUpdateQuoteEquipmentInput>,
  vendorOpportunityId: string
): Record<string, unknown> =>
  removeGraphQLMEtaFields({
    vendorOpportunityId,
    term: null,
    downPayment: null,
    interestRate: null,
    paymentAmount: null,
    contractStartDate: null,
    firstPaymentDueDate: null,
    fees: quote?.fees ?? [],
    equipmentTradeIns: quote?.equipmentTradeIns ?? [],
    deposit: quote?.deposit ?? null,
    attributes: quote?.attributes ?? [],
    aftermarketProducts: quote?.aftermarketProducts ?? [],
    rebates: quote?.rebates ?? [],
    cashBackAmounts: quote?.cashBackAmounts ?? [],
    equipment: [equipmentEntry],
  });

export const mapDealBuilderFormValuesToDesking = (
  reviewSend: DealBuilderFormValues['reviewSend'],
  savedFormData: FinanceProgramFormValues,
  equipmentId: string
): FinanceProgramFormValues => {
  const { sendVia, sendEmailTo, ccEmails, notesToBuyer } = reviewSend;
  const sendToContact = sendEmailTo?.[0];

  return {
    ...savedFormData,
    contact: {
      ...savedFormData.contact,
      phone: sendToContact?.phone ?? '',
      email: sendToContact?.email ?? '',
      title: sendToContact?.title ?? '',
      fullName: sendToContact?.fullName ?? '',
    },
    equipment: {
      ...savedFormData.equipment,
      selectedEquipment: equipmentId,
    },
    sendVia: { type: sendVia as FinanceProgramFormValues['sendVia']['type'] },
    ccEmail: (ccEmails ?? []).map(o => ({
      fullName: o.fullName,
      email: o.email,
      title: o.title,
      group: o.group as string as CcEmailOptionGroups,
    })),
    notesToBuyer: notesToBuyer ?? undefined,
  };
};

export const dealSummaryGetEquipmentTitle = (
  item: QuoteEquipmentResponse | QuoteEquipmentTradeInsResponse,
  equipmentData: Nullable<DealBuilderEquipmentSource[]>
): string => {
  const source = equipmentData?.find(e => e.equipmentId === item.equipmentId);
  const title = [source?.year, source?.make, source?.model].filter(Boolean).join(' ');
  return title || (item.stockNumber ?? item.vin ?? item.equipmentId);
};
